// ---------------------------------------------------------------------------
// FinTable.js — Sortable, paginated, clickable data table component
// Drop-in replacement for raw <table className="fin-table"> across all pages.
//
// Usage:
//   <FinTable
//     columns={[
//       { key: "trade_date", label: "Date", sortable: true },
//       { key: "quantity", label: "Qty", align: "right", sortable: true, fmt: v => Number(v).toLocaleString() },
//       { key: "action", label: "Action", render: (v, row) => <Badge>{v}</Badge> },
//       { key: "description", label: "Desc", truncate: 30 },
//     ]}
//     rows={data}
//     pageSize={25}
//     onRowClick={(row) => openDetail(row)}
//     emptyMessage="No records found."
//     actionColumn={(row) => <button onClick={() => edit(row)}>Edit</button>}
//   />
// ---------------------------------------------------------------------------
import React, { useState, useMemo } from "react";

// ── Helpers ──────────────────────────────────────────────────────────────────

function getNestedValue(obj, key) {
  if (!obj || !key) return undefined;
  return key.split(".").reduce((o, k) => (o != null ? o[k] : undefined), obj);
}

function compareValues(a, b, direction) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  // Try numeric comparison first
  const numA = Number(a);
  const numB = Number(b);
  if (!isNaN(numA) && !isNaN(numB)) {
    return direction === "asc" ? numA - numB : numB - numA;
  }

  // Date comparison (YYYY-MM-DD format)
  if (typeof a === "string" && /^\d{4}-\d{2}-\d{2}/.test(a)) {
    const dA = new Date(a).getTime();
    const dB = new Date(b).getTime();
    if (!isNaN(dA) && !isNaN(dB)) {
      return direction === "asc" ? dA - dB : dB - dA;
    }
  }

  // String comparison
  const strA = String(a).toLowerCase();
  const strB = String(b).toLowerCase();
  if (strA < strB) return direction === "asc" ? -1 : 1;
  if (strA > strB) return direction === "asc" ? 1 : -1;
  return 0;
}

// ── Sort indicator ───────────────────────────────────────────────────────────

function SortIcon({ direction }) {
  if (!direction) {
    return (
      <span style={{ opacity: 0.3, marginLeft: 4, fontSize: 10 }}>⇅</span>
    );
  }
  return (
    <span style={{ marginLeft: 4, fontSize: 10, color: "var(--fin-accent, #3b82f6)" }}>
      {direction === "asc" ? "▲" : "▼"}
    </span>
  );
}

// ── Truncated cell with tooltip ──────────────────────────────────────────────

function TruncatedCell({ value, maxLen }) {
  const str = value != null ? String(value) : "";
  if (str.length <= maxLen) return <>{str}</>;

  return (
    <span
      title={str}
      style={{ cursor: "help" }}
    >
      {str.slice(0, maxLen)}…
    </span>
  );
}

// ── Pagination controls ──────────────────────────────────────────────────────

function Pagination({ page, totalPages, totalRows, pageSize, onPageChange }) {
  if (totalPages <= 1) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalRows);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 14px",
        borderTop: "1px solid var(--fin-border, #262d3a)",
        fontFamily: "var(--fin-sans, -apple-system, sans-serif)",
        fontSize: 12,
        color: "var(--fin-text-dim, #7a8599)",
      }}
    >
      <span>
        {start}–{end} of {totalRows.toLocaleString()}
      </span>
      <div style={{ display: "flex", gap: 4 }}>
        <PaginationBtn
          label="«"
          disabled={page <= 1}
          onClick={() => onPageChange(1)}
        />
        <PaginationBtn
          label="‹"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        />
        <span
          style={{
            padding: "4px 10px",
            background: "var(--fin-accent, #3b82f6)",
            color: "#fff",
            borderRadius: 4,
            fontWeight: 600,
            minWidth: 28,
            textAlign: "center",
          }}
        >
          {page}
        </span>
        <PaginationBtn
          label="›"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        />
        <PaginationBtn
          label="»"
          disabled={page >= totalPages}
          onClick={() => onPageChange(totalPages)}
        />
      </div>
      <span>{totalPages} pages</span>
    </div>
  );
}

function PaginationBtn({ label, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "4px 8px",
        border: "1px solid var(--fin-border, #262d3a)",
        borderRadius: 4,
        background: disabled
          ? "transparent"
          : "var(--fin-surface-2, #1a1f2b)",
        color: disabled
          ? "var(--fin-border, #262d3a)"
          : "var(--fin-text-dim, #7a8599)",
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "var(--fin-sans, -apple-system, sans-serif)",
        fontSize: 12,
        fontWeight: 600,
        transition: "all 0.15s",
      }}
    >
      {label}
    </button>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// FinTable Component
// ═════════════════════════════════════════════════════════════════════════════

export default function FinTable({
  columns = [],
  rows = [],
  pageSize = 25,
  onRowClick,
  emptyMessage = "No records found.",
  loading = false,
  actionColumn,
  defaultSort,          // { key: "trade_date", direction: "desc" }
  stickyHeader = true,
}) {
  // ── Sort state ──
  const [sortKey, setSortKey] = useState(defaultSort?.key || null);
  const [sortDir, setSortDir] = useState(defaultSort?.direction || "asc");

  // ── Pagination state ──
  const [page, setPage] = useState(1);

  // Reset page when rows change
  const rowCount = rows.length;
  React.useEffect(() => setPage(1), [rowCount]);

  // ── Sort logic ──
  const handleSort = (key) => {
    if (sortKey === key) {
      // Cycle: asc → desc → none
      if (sortDir === "asc") {
        setSortDir("desc");
      } else {
        setSortKey(null);
        setSortDir("asc");
      }
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1);
  };

  // ── Processed rows (sorted + paginated) ──
  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) =>
      compareValues(getNestedValue(a, sortKey), getNestedValue(b, sortKey), sortDir)
    );
  }, [rows, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const pagedRows = useMemo(
    () => sortedRows.slice((page - 1) * pageSize, page * pageSize),
    [sortedRows, page, pageSize]
  );

  // ── Render ──
  if (loading) {
    return <div className="fin-loading">Loading…</div>;
  }

  if (rows.length === 0) {
    return <div className="fin-empty">{emptyMessage}</div>;
  }

  return (
    <div>
      <div className="fin-table-wrap">
        <table className="fin-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    textAlign: col.align || "left",
                    cursor: col.sortable ? "pointer" : "default",
                    userSelect: col.sortable ? "none" : undefined,
                    position: stickyHeader ? "sticky" : undefined,
                    top: stickyHeader ? 0 : undefined,
                    background: stickyHeader
                      ? "var(--fin-surface, #141820)"
                      : undefined,
                    zIndex: stickyHeader ? 2 : undefined,
                  }}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  {col.label}
                  {col.sortable && (
                    <SortIcon
                      direction={sortKey === col.key ? sortDir : null}
                    />
                  )}
                </th>
              ))}
              {actionColumn && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row, rowIdx) => (
              <tr
                key={row.id || rowIdx}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={{
                  cursor: onRowClick ? "pointer" : undefined,
                }}
              >
                {columns.map((col) => {
                  const rawVal = getNestedValue(row, col.key);
                  let display;

                  if (col.render) {
                    display = col.render(rawVal, row);
                  } else if (col.fmt) {
                    display = col.fmt(rawVal);
                  } else if (col.truncate && rawVal) {
                    display = (
                      <TruncatedCell
                        value={rawVal}
                        maxLen={col.truncate}
                      />
                    );
                  } else {
                    display = rawVal != null ? String(rawVal) : "—";
                  }

                  return (
                    <td
                      key={col.key}
                      style={{ textAlign: col.align || "left" }}
                      className={col.className}
                    >
                      {display}
                    </td>
                  );
                })}
                {actionColumn && (
                  <td onClick={(e) => e.stopPropagation()}>
                    {actionColumn(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        totalPages={totalPages}
        totalRows={rows.length}
        pageSize={pageSize}
        onPageChange={setPage}
      />
    </div>
  );
}
